import getSuggestions, {
  SmartyStreetsResponse,
  SmartyStreetsResponseItem,
} from 'features/signup/helpers/smartyStreets';
import { constructUrlWithParams } from 'features/signup/utils/signupUtils';
import { useContext, useEffect, useState } from 'react';
import { Headers } from 'shared/constants/headers';
import {
  EnvironmentContext,
  EnvironmentContextProps,
  getReferralHeaderUrl,
  getSmartyStreetsBaseUrl,
} from 'shared/providers/environment';
import getEnvironmentVar from 'shared/utils/getEnvironmentVar';
import { logBreadcrumb } from 'shared/utils/sentry/sentry';

const getAddressLine = (suggestion: SmartyStreetsResponseItem): string => {
  const entries =
    suggestion?.entries && suggestion?.entries > 1
      ? '(' + suggestion.entries + ' entries)'
      : '';
  return `${suggestion?.street_line ?? ''} ${suggestion?.secondary ?? ''} ${entries} ${suggestion?.city ?? ''}, ${suggestion?.state ?? ''} ${suggestion?.zipcode ?? ''}`;
};

const getSmartyStreetsResponse = async (
  headers: Headers,
  source: string,
  selectedSearchTerms?: SmartyStreetsResponseItem,
) => {
  let params: Record<string, unknown>;
  if (selectedSearchTerms) {
    const selectedValue = `${selectedSearchTerms.street_line} ${selectedSearchTerms.secondary} (${selectedSearchTerms.entries}) ${selectedSearchTerms.city} ${selectedSearchTerms.state} ${selectedSearchTerms.zipcode}`;
    params = {
      key: getEnvironmentVar('EXPO_SMARTY_STREETS_API_KEY'),
      search: source,
      selected: selectedValue,
    };
  } else {
    params = {
      key: getEnvironmentVar('EXPO_SMARTY_STREETS_API_KEY'),
      search: source,
      source: 'postal',
      max_results: 5,
    };
  }
  const SMARTY_STREETS_URL = constructUrlWithParams(
    getSmartyStreetsBaseUrl(),
    params,
  );
  let response: SmartyStreetsResponse = { suggestions: [] };
  try {
    response = await getSuggestions(SMARTY_STREETS_URL, headers);
  } catch (e) {
    logBreadcrumb({
      level: 'error',
      message: `Error fetching Smarty Streets response: ${e}`,
    });
  }
  return response;
};

const useSmartyStreets = (
  source: string,
  selectedSearchTerms?: SmartyStreetsResponseItem,
) => {
  const [suggestions, setSuggestions] = useState<SmartyStreetsResponseItem[]>(
    [],
  );
  const { currentEnv } =
    useContext<EnvironmentContextProps>(EnvironmentContext);

  useEffect(() => {
    const fetchData = async (
      selectedSearchTerms?: SmartyStreetsResponseItem,
    ) => {
      const response = await getSmartyStreetsResponse(
        {
          referer: getReferralHeaderUrl(currentEnv),
        },
        source,
        selectedSearchTerms,
      );
      const updatedItems =
        response?.suggestions?.map((item) => {
          return { ...item, address_line: getAddressLine(item) };
        }) || [];
      setSuggestions(updatedItems);
    };
    if (selectedSearchTerms?.entries && selectedSearchTerms.entries > 1) {
      void fetchData(selectedSearchTerms);
    } else if (source.length > 2) {
      void fetchData();
    } else {
      setSuggestions([]);
    }
  }, [source, selectedSearchTerms, currentEnv]);
  return {
    source,
    suggestions,
  };
};

export default useSmartyStreets;
